import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const CURVED_SCREENS_CONTENT: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "CurvedScreens",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Wedge Wire Screen Family",
      standardBadge: "Resistance Welded Profile Wire",
      heading: "Curved Screens",
      lead: "Engineered with precise radial curvature and resistance-welded profile wire, MutualFilter curved screens leverage fluid kinetic energy for continuous cross-flow dewatering, pulp fiber recovery, and mineral slurry classification without moving parts.",
      secondaryCta: {
        label: "Wedge Wire Overview",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
        alt: "Stainless steel curved wedge wire screen panel with side mounting bars",
      },
      cardNote: "Slot widths 0.02 to 5.0 mm",
      cardLink: {
        label: "Request a Quote",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Slot Width", value: "0.02 – 5.0 mm" },
        { label: "Arc Angles", value: "45° / 60° / 120°" },
        { label: "Screening Action", value: "Cross-Flow" },
        { label: "Alloy Grades", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Radial Arc Panels • Cross-Flow Dewatering",
      heading: "Hydrodynamic Thin-Film Slicing and Continuous Self-Cleaning",
      paragraphs: [
        "Curved wedge wire screens, commonly deployed in sieve bend housings, utilize stationary radial arc geometry to accomplish high-volume solid-liquid separation. As slurry flows gravitationally down the curved inner face, profile wires oriented perpendicular to the flow slice microscopic liquid layers through continuous slots.",
        "The continuous slot configuration features a distinctive two-point particle contact along the leading edges of the V-wire profiles. Beneath the surface aperture, inward-widening relief angles prevent near-size particles from jamming in the slots, providing dependable non-clogging performance in demanding slurries.",
        "Fabricated from corrosion-resistant AISI 304 and AISI 316L stainless steel, MutualFilter curved screens feature symmetrical radial profiles that allow 180-degree panel reversal, exposing fresh leading wire edges to double working service life in abrasive industrial duties.",
      ],
      keyPoints: [
        "Profile wires oriented perpendicular to slurry flow slice thin liquid films continuously with zero mechanical power.",
        "Two-point edge contact and widening relief angles ensure solids passing the aperture discharge freely without clogging.",
        "Symmetrical arc panels can be reversed 180° to expose fresh leading edges, doubling operational lifespan in abrasive slurries.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Flat-screen.jpg?v=8koqnxz00tf",
        alt: "Precision curved radial wedge wire screen surface for cross-flow separation",
      },
    },
    specTable: {
      heading: "Curved Screen Construction & Dimensional Parameters",
      intro: "Standard manufacturing parameters for curved radial sieve bend panels. Custom arc radiuses, framing flanges, and slot dimensions are available upon order specification.",
      columns: [
        { key: "parameter", label: "Specification Element", align: "left" },
        { key: "standard", label: "Standard Manufacturing Range", align: "right" },
        { key: "notes", label: "Engineering Execution", align: "left" },
      ],
      rows: [
        {
          parameter: "Slot Width Opening",
          standard: "0.02 mm – 5.0 mm",
          notes: "Continuous slot spacing verified along arc perimeter",
        },
        {
          parameter: "Standard Arc Angles",
          standard: "45°, 60°, 120°",
          notes: "Engineered to match stationary and gravity feed boxes",
        },
        {
          parameter: "Nominal Arc Radius",
          standard: "500 mm – 2030 mm",
          notes: "Calibrated curvature for uniform slurry acceleration",
        },
        {
          parameter: "Panel Width",
          standard: "300 mm – 2000 mm",
          notes: "Custom widths fabricated to housing tolerances",
        },
        {
          parameter: "Alloy Metallurgy",
          standard: "AISI 304, AISI 316, AISI 316L",
          notes: "Material test certificates available when specified in the order",
        },
        {
          parameter: "Framing Options",
          standard: "Side bars, angle flanges, rubber seals",
          notes: "Reversible mounting arrangements for rapid changeout",
        },
      ],
      caption: "All wedge wire curved panels are resistance-welded to support bars. Specific profile and support rod combinations selected by slurry burden.",
    },
    features: {
      heading: "Design Attributes & Mechanical Performance",
      intro: "Engineered details that differentiate MutualFilter curved screens in demanding separation applications.",
      items: [
        {
          title: "Passive Hydrodynamic Slicing",
          text: "Operates purely through fluid momentum and gravity, dewatering slurries efficiently without electric drives or rotating parts.",
        },
        {
          title: "V-Wire Inward Relief",
          text: "Progressively widening slot geometry ensures particles that clear the aperture do not lodge within the screen structure.",
        },
        {
          title: "180° Reversible Frame",
          text: "Symmetrical mounting allows operators to rotate panels end-for-end, presenting fresh cutting edges to double wear life.",
        },
        {
          title: "Resistance Fusion Welds",
          text: "Every intersection between surface V-wire and perpendicular support rod is fused by high-amperage resistance welding for structural integrity.",
        },
      ],
    },
    related: {
      heading: "Related Wedge Wire Screen Configurations",
      intro: "Explore alternative profile geometries and complete wedge wire separation assemblies across our product line.",
      cards: [
        {
          title: "Sieve Bend Screens",
          text: "Gravity-fed and pressure-fed curved sieve bend assemblies engineered for high-throughput slurry dewatering and classification.",
          to: "/SieveBendScreensPage",
          badge: "Curved Assembly",
          linkLabel: "View Sieve Bend Screens",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
            alt: "Stainless steel sieve bend screen panel",
          },
        },
        {
          title: "Flat Panel Screens",
          text: "Heavy-duty flat wedge wire panels for vibrating screens, static dewatering decks, and mineral slurry drainage.",
          to: "/FlatPanelScreensPage",
          badge: "Planar Alternative",
          linkLabel: "View Flat Panel Screens",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Flat wedge wire screen panel",
          },
        },
        {
          title: "Cylindrical Screens",
          text: "Rotary drum screens and basket cylinders featuring axial or circumferential wedge wire slot orientations.",
          to: "/CylindricalScreensPage",
          badge: "Rotary & Cylinder",
          linkLabel: "View Cylindrical Screens",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Cylindrical wedge wire screens",
          },
        },
        {
          title: "Screen Tubes",
          text: "Continuous slot screen pipes and heavy-wall tubes engineered for high collapse resistance in extraction and strainer duties.",
          to: "/ScreenTubesPage",
          badge: "Tubular Family",
          linkLabel: "View Screen Tubes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Stainless steel screen tubes and pipes",
          },
        },
        {
          title: "Filter Nozzles",
          text: "Threaded dome and cylindrical wedge wire nozzles for uniform fluid distribution and media retention in vessel underdrains.",
          to: "/FilterNozzlesPage",
          badge: "Underdrain Systems",
          linkLabel: "View Filter Nozzles",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Stainless steel wedge wire filter nozzles",
          },
        },
        {
          title: "Wedge Wire Screen Overview",
          text: "Comprehensive overview of profile wire geometry, slot specifications from 0.02 to 5.0 mm, and industrial configurations.",
          to: "/WedgeWireScreenPage",
          badge: "Parent Category",
          linkLabel: "View Wedge Wire Hub",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
            alt: "Wedge wire screen manufacturing overview",
          },
        },
      ],
    },
    cta: {
      id: "curved-screens-cta",
      eyebrow: "Application Engineering Support",
      heading: "Specify Curved Wedge Wire Screen Panels",
      lead: "Submit your arc radius, chord length, width, and slot opening requirements for prompt engineering review and quotation.",
      secondaryLabel: "Return to Wedge Wire Overview",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Standard alloys AISI 304 and AISI 316L. Inspection records and material test certificates available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "CurvedScreens",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "楔形丝滤网系列",
      standardBadge: "全熔电阻焊工艺型材丝",
      heading: "不锈钢弧形筛网与弧形筛板",
      lead: "MutualFilter 弧形筛网采用精密径向滚弯弧度与全熔电阻焊接工艺，充分利用物料重力与流体动能，在无动力运动部件的条件下实现连续切向水膜剥离、造纸纤维回收与矿物分级脱水。",
      secondaryCta: {
        label: "返回楔形丝滤网总览",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
        alt: "带有侧边安装加强条的不锈钢楔形丝弧形筛板（筛弯）",
      },
      cardNote: "缝隙宽度 0.02 至 5.0 mm",
      cardLink: {
        label: "获取工程报价",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "缝隙宽度", value: "0.02 – 5.0 mm" },
        { label: "标准弧度规格", value: "45° / 60° / 120°" },
        { label: "分离模式", value: "切向脱水 (Cross-Flow)" },
        { label: "标准材质", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "径向弧形筛面 • 切向横流水力脱水",
      heading: "流体动力切向水膜剥离与连续自释放防堵",
      paragraphs: [
        "弧形楔形丝筛板（常称为筛弯或弧形筛）利用静态固定径向弧形结构实现高通量固液分离。当矿浆或浆料依靠重力沿弯曲的内表面向下切向流动时，垂直于流向布置的梯形截面丝形成锋利的迎水刀口，连续切削并剥离底层薄层水膜。",
        "连续微米级缝隙在 V 型丝表面形成微触双点开孔结构。穿过表层筛口后的缝隙向后逐渐扩大形成自释放通道，从根本上防止临界尺寸固体颗粒在缝道深处发生机械挤压卡塞，确保苛刻工况下的长期稳定通透。",
        "选用耐腐蚀 AISI 304 及 AISI 316L 优质不锈钢精密制作。对称的径向圆弧与端部结构支持筛板进行 180 度翻转颠倒安装，重新利用背向未磨损的锋利迎水刃口，使高磨损矿浆工况下的使用寿命成倍延长。",
      ],
      keyPoints: [
        "垂直于料流方向的 V 型型材丝形成连续水膜切削剥离，依靠重力与流体动能实现高速固液分离。",
        "微触双点开孔与后向释放角确保固体微粒穿过表面后顺畅滑落，彻底杜绝深层机械卡塞。",
        "对称弧形结构支持筛板 180 度翻转安装，重新启用锋利迎水面，在高磨损工况下使有效寿命倍增。",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Flat-screen.jpg?v=8koqnxz00tf",
        alt: "精密滚弯径向弧形楔形丝筛网表面",
      },
    },
    specTable: {
      heading: "弧形筛板结构与制造技术参数表",
      intro: "代表性弧形筛板制造技术范围。支持根据用户进料箱法兰及箱体结构定制圆弧半径、弦长、包边形式与缝隙精度。",
      columns: [
        { key: "parameter", label: "结构参数项目", align: "left" },
        { key: "standard", label: "标准制造范围", align: "right" },
        { key: "notes", label: "工程说明", align: "left" },
      ],
      rows: [
        {
          parameter: "筛缝开孔宽度",
          standard: "0.02 mm – 5.0 mm",
          notes: "全圆弧周向缝隙公差均匀稳定受控",
        },
        {
          parameter: "标准圆弧包角",
          standard: "45°, 60°, 120°",
          notes: "适配主流重力与压力式进料筛箱",
        },
        {
          parameter: "公称曲率半径",
          standard: "500 mm – 2030 mm",
          notes: "精准校准弧度确保物料切向加速平顺",
        },
        {
          parameter: "筛板有效宽度",
          standard: "300 mm – 2000 mm",
          notes: "按安装箱体内径严格公差加工",
        },
        {
          parameter: "制造材料牌号",
          standard: "AISI 304, AISI 316, AISI 316L",
          notes: "材质证明书可按订单技术要求提供",
        },
        {
          parameter: "侧向固定包边",
          standard: "加强扁钢、角钢法兰、胶条卡槽",
          notes: "支持对称翻转快速拆装固定方式",
        },
      ],
      caption: "表面楔形丝与下部加强支撑杆在每一交点均牢固电阻熔焊。具体丝型与支撑杆选型依物料负荷匹配。",
    },
    features: {
      heading: "技术特性与结构优势",
      intro: "MutualFilter 弧形筛板在工业连续脱水与颗粒筛分中的核心工程特点。",
      items: [
        {
          title: "无动力流体切削脱水",
          text: "完全利用流体自身动能与重力流动，实现高效固液脱水分离，系统无运动部件且维护简便。",
        },
        {
          title: "内宽自释放防堵缝隙",
          text: "前窄后宽的 V 型几何断开角，进入缝口的微粒能够迅速随水流脱出，杜绝卡孔结垢。",
        },
        {
          title: "180 度翻转双倍寿命",
          text: "结构完全对称，当迎流面磨损后可上下调转方向安装，利用全新刃口继续工作。",
        },
        {
          title: "全熔电阻牢固焊接",
          text: "表面筛丝与横向支撑筋条采用大电流电阻焊逐点融合，抗震动疲劳与物料冲击性能优异。",
        },
      ],
    },
    related: {
      heading: "相关楔形丝滤网产品系列",
      intro: "对比平整筛面、圆筒旋转筛及烛式筛管，选择最适配您工况的固液分离方案。",
      cards: [
        {
          title: "弧形筛弯总成",
          text: "针对重力或压力送料箱体成套设计的弧形筛弯总成，适用于淀粉洗涤与选煤脱水。",
          to: "/SieveBendScreensPage",
          badge: "筛弯成套结构",
          linkLabel: "查看筛弯总成",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
            alt: "不锈钢筛弯弧形筛板总成",
          },
        },
        {
          title: "平面型楔形丝筛板",
          text: "重型加强框架结构，广泛应用于振动筛机筛板、静态脱水床与重力矿浆分级脱介。",
          to: "/FlatPanelScreensPage",
          badge: "平面结构对比",
          linkLabel: "查看平面筛板",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "平面型楔形丝筛板",
          },
        },
        {
          title: "圆柱型筛筒与转鼓筛",
          text: "连续全焊接圆柱滤筒，涵盖内圆轴向缝隙（刮刀滤器）与外圆周向缝隙滚筒筛网。",
          to: "/CylindricalScreensPage",
          badge: "圆柱与滚筒",
          linkLabel: "查看圆柱筛筒",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "圆柱型楔形丝滤筒组件",
          },
        },
        {
          title: "连续缝隙筛管与滤芯",
          text: "高抗压连续缝隙筛管与深井滤水管，具备高抗压坍塌强度与微米级过滤精度。",
          to: "/ScreenTubesPage",
          badge: "管状滤元系列",
          linkLabel: "查看筛管系列",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "不锈钢连续缝隙筛管与烛式滤芯",
          },
        },
        {
          title: "楔形丝滤水帽与喷嘴",
          text: "螺纹连接圆顶与圆柱型滤水帽，用于水处理树脂交换柱与假底均匀布水及介质截留。",
          to: "/FilterNozzlesPage",
          badge: "布水截留元件",
          linkLabel: "查看滤水帽",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "不锈钢楔形丝滤水帽与喷嘴",
          },
        },
        {
          title: "楔形丝滤网总览",
          text: "查阅约翰逊楔形丝全系列截面尺寸、0.02 至 5.0 mm 缝隙标准与工业应用指南。",
          to: "/WedgeWireScreenPage",
          badge: "所属分类",
          linkLabel: "返回楔形丝总览",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
            alt: "楔形丝筛网生产加工总览",
          },
        },
      ],
    },
    cta: {
      id: "curved-screens-cta",
      eyebrow: "技术选型与规格咨询",
      heading: "获取弧形筛板工程技术方案与报价",
      lead: "请提供您的弧度包角、曲率半径、有效宽度及目标缝隙尺寸，我们的工程技术人员将协助复核计算并提供专属方案。",
      secondaryLabel: "返回楔形丝滤网总览",
      secondaryTo: "/WedgeWireScreenPage",
      note: "标准制造材质 AISI 304 与 AISI 316L。质检记录与材质检测证明可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "CurvedScreens",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Familia de alambre en cuña",
      standardBadge: "Alambre perfilado soldado por resistencia",
      heading: "Cribas curvas",
      lead: "Diseñadas con curvatura radial precisa y alambre perfilado soldado por resistencia, las cribas curvas aprovechan la energía cinética del fluido para deshidratación tangencial continua, recuperación de fibras y clasificación de pulpas sin componentes motrices.",
      secondaryCta: {
        label: "Malla en cuña general",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
        alt: "Panel de criba curva de alambre en cuña de acero inoxidable con barras de fijación laterales",
      },
      cardNote: "Ranura de 0,02 a 5,0 mm",
      cardLink: {
        label: "Solicitar cotización",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Ancho de ranura", value: "0,02 – 5,0 mm" },
        { label: "Ángulos de arco", value: "45° / 60° / 120°" },
        { label: "Acción de cribado", value: "Flujo cruzado" },
        { label: "Aleaciones", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Paneles de arco radial • Filtración tangencial",
      heading: "Cizallamiento hidrodinámico de lámina fluida y autolimpieza continua",
      paragraphs: [
        "Las cribas curvas de alambre en cuña emplean una geometría estática de arco radial para lograr una separación sólido-líquido de gran caudal. A medida que la suspensión fluye por gravedad a lo largo de la cara cóncava, los alambres en perfil en V orientados perpendicularmente cortan finas capas de líquido a través de ranuras continuas.",
        "La ranura continua ofrece contacto en dos puntos en los bordes de ataque del perfil. Por debajo de la superficie, el ángulo de alivio ensanchado hacia el interior evita que las partículas de tamaño crítico queden encajadas, ofreciendo un comportamiento antiobstrucción constante.",
        "Fabricadas en acero inoxidable AISI 304 y AISI 316L, su diseño simétrico permite rotar el panel 180 grados para presentar aristas de corte nuevas, duplicando la vida útil ante lodos abrasivos.",
      ],
      keyPoints: [
        "Los perfiles de alambre perpendiculares al flujo cortan láminas delgadas de líquido de forma continua y sin consumo de energía motriz.",
        "El contacto en dos puntos y el ángulo de alivio posterior aseguran que los sólidos descarguen libremente sin atascos.",
        "Los paneles simétricos se pueden invertir 180° para presentar aristas nuevas, duplicando la durabilidad en pulpas abrasivas.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Flat-screen.jpg?v=8koqnxz00tf",
        alt: "Superficie de criba curva de alambre en cuña para separación tangencial",
      },
    },
    specTable: {
      heading: "Parámetros dimensionales de cribas curvas",
      intro: "Especificaciones estándar de fabricación para paneles curvos. Radios y marcos especiales disponibles bajo pedido.",
      columns: [
        { key: "parameter", label: "Elemento de especificación", align: "left" },
        { key: "standard", label: "Rango de fabricación estándar", align: "right" },
        { key: "notes", label: "Detalles de ingeniería", align: "left" },
      ],
      rows: [
        {
          parameter: "Ancho de abertura de ranura",
          standard: "0,02 mm – 5,0 mm",
          notes: "Apertura continua uniforme a lo largo del arco",
        },
        {
          parameter: "Ángulos de arco estándar",
          standard: "45°, 60°, 120°",
          notes: "Compatibles con cajas de alimentación por gravedad",
        },
        {
          parameter: "Radio de curvatura nominal",
          standard: "500 mm – 2030 mm",
          notes: "Curvatura calibrada para aceleración uniforme de la suspensión",
        },
        {
          parameter: "Ancho de panel",
          standard: "300 mm – 2000 mm",
          notes: "Ajuste estricto a las tolerancias de la carcasa",
        },
        {
          parameter: "Calidades de aleación",
          standard: "AISI 304, AISI 316, AISI 316L",
          notes: "Certificados de material disponibles cuando se especifiquen en el pedido",
        },
        {
          parameter: "Opciones de fijación",
          standard: "Barras laterales, bridas angulares",
          notes: "Montaje reversible para mantenimiento rápido",
        },
      ],
      caption: "Alambres en cuña soldados por resistencia a varillas transversales de soporte.",
    },
    features: {
      heading: "Atributos mecánicos y ventajas de diseño",
      intro: "Detalles técnicos que distinguen a las cribas curvas en aplicaciones de deshidratación industrial.",
      items: [
        {
          title: "Separación hidrodinámica pasiva",
          text: "Funciona mediante la gravedad y la inercia del fluido, desaguando eficazmente sin motores ni elementos rotativos.",
        },
        {
          title: "Alivio interior en V",
          text: "El ensanchamiento progresivo de la ranura asegura que los sólidos no se encajen en el cuerpo del panel.",
        },
        {
          title: "Diseño reversible a 180°",
          text: "Permite girar el panel para aprovechar bordes no desgastados, duplicando el tiempo operativo útil.",
        },
        {
          title: "Soldadura por electrofusión",
          text: "Fusión continua en cada cruce de alambre y varilla de soporte para soportar vibraciones e impacto.",
        },
      ],
    },
    related: {
      heading: "Variantes relacionadas de alambre en cuña",
      intro: "Conozca otras configuraciones de cribado de alambre perfilado para procesos industriales.",
      cards: [
        {
          title: "Sieve Bend Screens",
          text: "Conjuntos de cribas curvas estáticas para deshidratación y clasificación de pulpas minerales y féculas.",
          to: "/SieveBendScreensPage",
          badge: "Conjuntos curvos",
          linkLabel: "Ver Sieve Bend",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
            alt: "Panel de criba tamiz curva",
          },
        },
        {
          title: "Cribas planas de alambre en cuña",
          text: "Paneles planos reforzados para cribas vibratorias, escurridores y mesas estáticas de lavado.",
          to: "/FlatPanelScreensPage",
          badge: "Alternativa plana",
          linkLabel: "Ver paneles planos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Panel plano de alambre en cuña",
          },
        },
        {
          title: "Cilindros de alambre en cuña",
          text: "Tambores giratorios y cestas cilíndricas con ranura axial o circunferencial para filtrado continuo.",
          to: "/CylindricalScreensPage",
          badge: "Cilindros y tambores",
          linkLabel: "Ver cilindros",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Cilindros de alambre en cuña",
          },
        },
        {
          title: "Tubos de filtración en cuña",
          text: "Tubos con ranura continua y alta resistencia al aplastamiento para pozos y captación de agua.",
          to: "/ScreenTubesPage",
          badge: "Elementos tubulares",
          linkLabel: "Ver tubos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Tubos de alambre en cuña",
          },
        },
        {
          title: "Boquillas y toberas de filtrado",
          text: "Boquillas roscadas para distribución homogénea y retención de resinas en fondos de recipientes.",
          to: "/FilterNozzlesPage",
          badge: "Sistemas de drenaje",
          linkLabel: "Ver boquillas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Boquillas de alambre en cuña",
          },
        },
        {
          title: "Malla en cuña general",
          text: "Visión global de geometrías de alambre, ranuras de 0,02 a 5,0 mm y configuraciones industriales.",
          to: "/WedgeWireScreenPage",
          badge: "Categoría principal",
          linkLabel: "Ver categoría general",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
            alt: "Fabricación de mallas en cuña",
          },
        },
      ],
    },
    cta: {
      id: "curved-screens-cta",
      eyebrow: "Soporte de ingeniería",
      heading: "Consulte especificaciones de cribas curvas",
      lead: "Indíquenos el radio de curvatura, ángulo de arco, ancho y ranura deseados para una propuesta técnica personalizada.",
      secondaryLabel: "Volver a malla en cuña",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Aleaciones estándar AISI 304 y AISI 316L. Certificados de materiales disponibles bajo especificación en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "CurvedScreens",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Spaltsieb Produktfamilie",
      standardBadge: "Widerstandsgeschweißtes Drahtprofil",
      heading: "Gekrümmte Spaltsiebe (Bogensiebe)",
      lead: "Gefertigt mit präziser radialer Bogenkrümmung und widerstandsgeschweißtem Spaltsiebprofil nutzen Bogensiebe die kinetische Strömungsenergie zur kontinuierlichen Entwässerung, Faseraufbereitung und Mineralsuspensionstrennung ohne bewegte Maschinenteile.",
      secondaryCta: {
        label: "Spaltsiebe Übersicht",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
        alt: "Edelstahl-Spaltsiebbogen mit seitlichen Montageleisten für Bogensiebe",
      },
      cardNote: "Spaltweiten 0,02 bis 5,0 mm",
      cardLink: {
        label: "Angebot anfordern",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Spaltweite", value: "0,02 – 5,0 mm" },
        { label: "Bogenwinkel", value: "45° / 60° / 120°" },
        { label: "Trennverfahren", value: "Querstrom (Cross-Flow)" },
        { label: "Werkstoffe", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Radiale Bogensegmente • Querstrom-Trennung",
      heading: "Hydrodynamisches Filmschälen und verstopfungsfreie Selbstreinigung",
      paragraphs: [
        "Gekrümmte Spaltsiebböden nutzen eine feststehende radiale Bogengeometrie für die kontinuierliche Fest-Flüssig-Trennung hoher Volumenströme. Fließt die Suspension schwerkraftgetrieben über die gekrümmte Siebfläche, schälen die quer zur Fließrichtung stehenden V-Drähte kontinuierlich dünne Flüssigkeitsschichten durch die Spalten ab.",
        "Der durchgehende Spalt besitzt eine Zweipunktauflage an den Kanten des V-förmigen Profildrahts. Dahinter erweitert sich der Freiwinkel trichterförmig nach innen, wodurch klemmende Feststoffpartikel zuverlässig abfließen und ein Verstopfen vermieden wird.",
        "Hergestellt aus den korrosionsbeständigen Edelstählen AISI 304 und AISI 316L, ermöglicht die symmetrische Bogenform das Wenden um 180 Grad, um unverbrauchte Schnittkanten zu nutzen und die Standzeit bei abrasivem Schlamm zu verdoppeln.",
      ],
      keyPoints: [
        "Quer zur Strömungsrichtung angeordnete Profildrähte schälen dünne Flüssigkeitsfilme kontinuierlich und ohne mechanischen Antrieb ab.",
        "Zweipunktkontakt und erweiterter Freiwinkel gewährleisten ungehinderten Feststoffaustrag ohne Verstopfungsrisiko.",
        "Symmetrische Bogensegmente lassen sich um 180° wenden, um neue Schnittkanten zu nutzen und die Standzeit bei abrasiven Schlämmen zu verdoppeln.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Flat-screen.jpg?v=8koqnxz00tf",
        alt: "Präzisionsgekrümmter Spaltsiebboden für die Querstromfiltration",
      },
    },
    specTable: {
      heading: "Technische Kennwerte von Bogensieb-Segmenten",
      intro: "Standardisierte Fertigungsbereiche für Spaltsieb-Bogensegmente. Kundenspezifische Radien, Bogenmaße und Fassungen auf Anfrage.",
      columns: [
        { key: "parameter", label: "Parameter", align: "left" },
        { key: "standard", label: "Standard-Fertigungsbereich", align: "right" },
        { key: "notes", label: "Konstruktive Ausführung", align: "left" },
      ],
      rows: [
        {
          parameter: "Spaltweitenbereich",
          standard: "0,02 mm – 5,0 mm",
          notes: "Gleichmäßige Spaltweiten über den gesamten Bogen",
        },
        {
          parameter: "Standard-Bogenwinkel",
          standard: "45°, 60°, 120°",
          notes: "Passend für gebräuchliche Einlaufgehäuse",
        },
        {
          parameter: "Bogenradius (Nennmaß)",
          standard: "500 mm – 2030 mm",
          notes: "Präzise Krümmung für kontinuierliche Strömungsbeschleunigung",
        },
        {
          parameter: "Siebbreite",
          standard: "300 mm – 2000 mm",
          notes: "Gefertigt nach Gehäusetoleranzen",
        },
        {
          parameter: "Edelstahllegierungen",
          standard: "AISI 304, AISI 316, AISI 316L",
          notes: "Werksprüfzeugnisse auf Kundenanforderung lieferbar",
        },
        {
          parameter: "Rahmenvarianten",
          standard: "Seitliche Klemmleisten, Flansche",
          notes: "Symmetrische Montage für schnellen Richtungswechsel",
        },
      ],
      caption: "V-Profildrähte werden durch Widerstandsschweißung mit Querstäben verschweißt.",
    },
    features: {
      heading: "Konstruktionsmerkmale und Werkstoffeigenschaften",
      intro: "Wesentliche Konstruktionsdetails von gekrümmten Spaltsieben für industrielle Entwässerungsprozesse.",
      items: [
        {
          title: "Passive Hydrodynamik",
          text: "Arbeitet rein durch die Strömungsenergie des Mediums und Schwerkraft, ohne Motoren oder drehende Einbauten.",
        },
        {
          title: "Trichterförmiger Freiwinkel",
          text: "Der nach innen breiter werdende Spalt verhindert das Festsetzen von Partikeln im Siebkörper.",
        },
        {
          title: "180°-Wendesystem",
          text: "Das Segment lässt sich umdrehen, um unverbrauchte Schneidkanten dem Fluss auszusetzen.",
        },
        {
          title: "Widerstands-Verschweißung",
          text: "Jeder Drahtkreuzungspunkt ist stoffschlüssig verschweißt und widersteht starken Schwingungsbelastungen.",
        },
      ],
    },
    related: {
      heading: "Verwandte Spaltsieb-Ausführungen",
      intro: "Entdecken Sie weitere Bauformen unserer widerstandsgeschweißten Spaltsieb-Produktpalette.",
      cards: [
        {
          title: "Bogensiebe (Sieve Bend)",
          text: "Komplette Bogensiebeinheiten für stationäre Schwerkraft- und Druckspeisung in der Faser- und Stärketrennung.",
          to: "/SieveBendScreensPage",
          badge: "Bogensieb-System",
          linkLabel: "Bogensiebe ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
            alt: "Edelstahl-Bogensieb Segment",
          },
        },
        {
          title: "Flache Spaltsiebböden",
          text: "Robuste ebene Siebböden für Vibrationsmaschinen, Entwässerungsrinnen und statische Trennstufen.",
          to: "/FlatPanelScreensPage",
          badge: "Ebene Ausführung",
          linkLabel: "Flachsiebe ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Flacher Spaltsiebboden",
          },
        },
        {
          title: "Spaltsiebzylinder",
          text: "Rund geschweißte Spaltsiebkörbe und Trommelsiebe mit axialer oder zirkularer Spaltanordnung.",
          to: "/CylindricalScreensPage",
          badge: "Zylinder & Trommeln",
          linkLabel: "Zylinder ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Spaltsiebzylinder",
          },
        },
        {
          title: "Spaltsiebrohre",
          text: "Druckfeste Filterkerzen und Brunnenfilterrohre mit durchgehendem Spalt für hohe Kollapsfestigkeit.",
          to: "/ScreenTubesPage",
          badge: "Rohrelemente",
          linkLabel: "Siebrohre ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Edelstahl-Spaltsiebrohre",
          },
        },
        {
          title: "Spaltsieb-Filterdüsen",
          text: "Düsenböden zur homogenen Flüssigkeitsverteilung und Harzrückhaltung in Filterbehältern.",
          to: "/FilterNozzlesPage",
          badge: "Drainagesysteme",
          linkLabel: "Filterdüsen ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Spaltsieb-Filterdüsen",
          },
        },
        {
          title: "Spaltsiebe Übersicht",
          text: "Gesamtübersicht aller V-Draht-Profilgeometrien, Spaltweiten von 0,02 bis 5,0 mm und Anwendungsfelder.",
          to: "/WedgeWireScreenPage",
          badge: "Hauptkategorie",
          linkLabel: "Zur Hauptkategorie",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
            alt: "Spaltsiebe Fertigung und Übersicht",
          },
        },
      ],
    },
    cta: {
      id: "curved-screens-cta",
      eyebrow: "Technische Beratung",
      heading: "Gekrümmte Spaltsiebe anfragen",
      lead: "Senden Sie uns Ihre Anforderungen bezüglich Radius, Bogenwinkel, Spaltweite und Werkstoff für ein detailliertes Angebot.",
      secondaryLabel: "Zurück zu Spaltsiebe",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Standardwerkstoffe AISI 304 und AISI 316L. Materialprüfzeugnisse verfügbar, wenn im Auftrag vereinbart.",
    },
  },
  fr: {
    breadcrumbKey: "CurvedScreens",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Gamme fils profilés en cuña",
      standardBadge: "Fil profilé soudé par résistance",
      heading: "Grilles courbes en fil profilé",
      lead: "Conçues avec une courbure radiale précise et un profilé en V soudé par résistance, les grilles courbes exploitent l'énergie cinétique du fluide pour l'égouttage continu, la récupération de fibres et la classification minérale sans moteur mécanique.",
      secondaryCta: {
        label: "Aperçu fils profilés",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
        alt: "Panneau de grille courbe en fil profilé inox avec barres de fixation latérales",
      },
      cardNote: "Ouvertures de 0,02 à 5,0 mm",
      cardLink: {
        label: "Demander un devis",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Largeur de fente", value: "0,02 – 5,0 mm" },
        { label: "Angles d'arc", value: "45° / 60° / 120°" },
        { label: "Principe", value: "Flux croisé (Cross-Flow)" },
        { label: "Nuances d'alliage", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Panneaux courbes radiaux • Égouttage tangentiel",
      heading: "Cisaillement hydrodynamique de film liquide et autonettoyage continu",
      paragraphs: [
        "Les grilles courbes en fil profilé utilisent une surface courbe stationnaire pour réaliser une séparation solide-liquide à fort débit. Lorsque la pulpe s'écoule par gravité sur la face concave, les fils profilés perpendiculaires au flux cisaillent continuellement de très fines couches liquides à travers les fentes régulières.",
        "La fente continue présente un contact en deux points sur les arêtes d'attaque. En dessous de la face active, les fentes s'évasent vers l'intérieur, permettant aux particules traversant l'ouverture de s'évacuer immédiatement sans se coincer dans la structure.",
        "Fabriqués en acier inoxydable AISI 304 et AISI 316L, ces panneaux symétriques peuvent être pivotés de 180 degrés pour exposer des arêtes de fil intactes, doublant ainsi leur durée de fonctionnement en milieu abrasif.",
      ],
      keyPoints: [
        "Les fils profilés perpendiculaires au flux cisaillent les films liquides en continu sans apport d'énergie mécanique.",
        "Le contact en deux points et le dépouillement arrière assurent l'évacuation libre des solides sans colmatage.",
        "Les panneaux arqués symétriques peuvent être retournés à 180° pour réutiliser de nouvelles arêtes d'attaque en milieu abrasif.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Flat-screen.jpg?v=8koqnxz00tf",
        alt: "Surface de grille courbe en fil profilé pour égouttage transversal",
      },
    },
    specTable: {
      heading: "Paramètres dimensionnels des grilles courbes",
      intro: "Plages standard de fabrication pour grilles courbes industrielles. Rayons et ferrures adaptés aux plans de montage.",
      columns: [
        { key: "parameter", label: "Élément technique", align: "left" },
        { key: "standard", label: "Plage de fabrication standard", align: "right" },
        { key: "notes", label: "Exécution mécanique", align: "left" },
      ],
      rows: [
        {
          parameter: "Largeur de fente",
          standard: "0,02 mm – 5,0 mm",
          notes: "Écartement constant vérifié sur tout l'arc",
        },
        {
          parameter: "Angles d'arc courants",
          standard: "45°, 60°, 120°",
          notes: "Compatibles avec les caissons d'alimentation gravitaires",
        },
        {
          parameter: "Rayon de courbure nominal",
          standard: "500 mm – 2030 mm",
          notes: "Profil calculé pour une accélération hydraulique stable",
        },
        {
          parameter: "Largeur de grille",
          standard: "300 mm – 2000 mm",
          notes: "Tolérances adaptées au logement récepteur",
        },
        {
          parameter: "Nuances métalliques",
          standard: "AISI 304, AISI 316, AISI 316L",
          notes: "Certificats matière fournis lorsque spécifiés dans la commande",
        },
        {
          parameter: "Types de rives",
          standard: "Barres méplat, cornières",
          notes: "Montage réversible pour échange rapide",
        },
      ],
      caption: "Fils profilés soudés par résistance aux barres transversales d'ossature.",
    },
    features: {
      heading: "Propriétés mécaniques et atouts d'ingénierie",
      intro: "Caractéristiques techniques assurant la fiabilité des grilles courbes en milieu industriel.",
      items: [
        {
          title: "Séparation hydrodynamique",
          text: "Tire parti de l'énergie du flux et de la gravité pour égoutter efficacement sans pièces rotatives.",
        },
        {
          title: "Dégagement profilé en V",
          text: "L'évasement vers l'arrière élimine tout risque d'encrassement des solides entre les spires de fil.",
        },
        {
          title: "Panneau réversible 180°",
          text: "La symétrie permet de retourner le panneau afin d'offrir des arêtes de coupe neuves.",
        },
        {
          title: "Soudure par fusion continue",
          text: "Liaison électrosoudée à haute intensité sur chaque point de contact pour une résistance accrue aux vibrations.",
        },
      ],
    },
    related: {
      heading: "Configurations associées en fil profilé",
      intro: "Découvrez nos autres équipements en fil profilé pour les procédés de filtration et de tamisage.",
      cards: [
        {
          title: "Grilles tamisantes courbes (Sieve Bend)",
          text: "Ensembles complets pour l'égouttage de boues et la séparation de fibres en amidonnerie et papeterie.",
          to: "/SieveBendScreensPage",
          badge: "Ensemble courbé",
          linkLabel: "Voir les grilles Sieve Bend",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
            alt: "Panneau de grille courbe Sieve Bend",
          },
        },
        {
          title: "Grilles planes en fil profilé",
          text: "Panneaux plans renforcés pour tables vibrantes, égouttage statique et criblage minier.",
          to: "/FlatPanelScreensPage",
          badge: "Variante plane",
          linkLabel: "Voir les grilles planes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Panneau plan en fil profilé",
          },
        },
        {
          title: "Cylindres et tambours filtrants",
          text: "Cylindres en fil profilé à fente axiale ou circonférentielle pour filtres rotatifs et paniers.",
          to: "/CylindricalScreensPage",
          badge: "Cylindres & tambours",
          linkLabel: "Voir les cylindres",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Cylindres en fil profilé",
          },
        },
        {
          title: "Tubes et crépines en fil profilé",
          text: "Tubes filtrants à fente continue haute résistance à l'écrasement pour forages et captages.",
          to: "/ScreenTubesPage",
          badge: "Éléments tubulaires",
          linkLabel: "Voir les tubes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Tubes de filtration en fil profilé",
          },
        },
        {
          title: "Crépines et buses de filtration",
          text: "Buses vissées pour planchers filtrants, distribution homogène et rétention de résines.",
          to: "/FilterNozzlesPage",
          badge: "Distribution & drainage",
          linkLabel: "Voir les buses",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Buses en fil profilé",
          },
        },
        {
          title: "Aperçu fils profilés",
          text: "Consultez l'ensemble de notre gamme en fil profilé, ouvertures de 0,02 à 5,0 mm et applications.",
          to: "/WedgeWireScreenPage",
          badge: "Catégorie parente",
          linkLabel: "Voir la gamme complète",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
            alt: "Fabrication de grilles en fil profilé",
          },
        },
      ],
    },
    cta: {
      id: "curved-screens-cta",
      eyebrow: "Conseil technique",
      heading: "Consultez-nous pour vos grilles courbes",
      lead: "Transmettez-nous votre rayon, angle d'arc, largeur et ouverture de fente pour obtenir une offre technique chiffrée.",
      secondaryLabel: "Retour aux fils profilés",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Nuances standard AISI 304 et AISI 316L. Certificats matière disponibles sur demande lors de la commande.",
    },
  },
  pt: {
    breadcrumbKey: "CurvedScreens",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Família de arame em cunha",
      standardBadge: "Arame perfilado soldado por resistência",
      heading: "Telas curvas de arame em cunha",
      lead: "Projetadas com curvatura radial precisa e arame perfilado soldado por resistência, as telas curvas utilizam a energia cinética do fluido para desaguamento tangencial contínuo, lavagem de amido e classificação de polpas sem peças móveis.",
      secondaryCta: {
        label: "Visão geral arame em cunha",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
        alt: "Painel de tela curva de arame em cunha de aço inoxidável com barras de fixação laterais",
      },
      cardNote: "Fendas de 0,02 a 5,0 mm",
      cardLink: {
        label: "Solicitar cotação",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Largura de fenda", value: "0,02 – 5,0 mm" },
        { label: "Ângulos de arco", value: "45° / 60° / 120°" },
        { label: "Ação de separação", value: "Fluxo cruzado" },
        { label: "Ligas de aço", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Painéis de arco radial • Filtração tangencial",
      heading: "Corte hidrodinâmico de película líquida e autolimpeza contínua",
      paragraphs: [
        "As telas curvas de arame em cunha utilizam uma superfície cilíndrica estacionária para processar grandes vazões de separação sólido-líquido. Conforme o efluente desce por gravidade pela face côncava, os arames com perfil em V perpendiculares ao fluxo cortam lâminas líquidas delgadas através das fendas.",
        "A fenda contínua apresenta contato em dois pontos nas arestas do arame. Abaixo da superfície, o ângulo de alívio que se alarga para o interior impede que partículas próximas do tamanho da fenda fiquem presas, assegurando vazão sem entupimentos.",
        "Fabricadas em aço inoxidável resistente à corrosão AISI 304 e AISI 316L, o formato simétrico permite girar o painel em 180 graus para expor bordas novas e não desgastadas, duplicando o tempo de operação em polpas abrasivas.",
      ],
      keyPoints: [
        "Arames perfilados perpendiculares ao fluxo cortam continuamente películas de líquido sem gasto de energia mecânica.",
        "Contato em dois pontos e ângulo de alívio traseiro garantem que os sólidos descarreguem livremente sem entupimento.",
        "Painéis de arco simétrico podem ser invertidos em 180° para expor novas arestas de corte, duplicando a vida útil em polpas abrasivas.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Flat-screen.jpg?v=8koqnxz00tf",
        alt: "Superfície de tela curva em cunha para desaguamento tangencial",
      },
    },
    specTable: {
      heading: "Parâmetros dimensionais de telas curvas",
      intro: "Especificações de fabricação para painéis curvos. Raios de curvatura e molduras especiais sob encomenda.",
      columns: [
        { key: "parameter", label: "Elemento de especificação", align: "left" },
        { key: "standard", label: "Faixa de fabricação padrão", align: "right" },
        { key: "notes", label: "Execução mecânica", align: "left" },
      ],
      rows: [
        {
          parameter: "Largura de fenda",
          standard: "0,02 mm – 5,0 mm",
          notes: "Abertura uniforme ao longo de todo o perímetro do arco",
        },
        {
          parameter: "Ângulos de arco padrão",
          standard: "45°, 60°, 120°",
          notes: "Adequados para caixas de alimentação por gravidade",
        },
        {
          parameter: "Raio de curvatura nominal",
          standard: "500 mm – 2030 mm",
          notes: "Curvatura calibrada para aceleração uniforme do fluxo",
        },
        {
          parameter: "Largura do painel",
          standard: "300 mm – 2000 mm",
          notes: "Ajustada às tolerâncias da caixa receptora",
        },
        {
          parameter: "Ligas de aço",
          standard: "AISI 304, AISI 316, AISI 316L",
          notes: "Certificados de materiais fornecidos quando especificados no pedido",
        },
        {
          parameter: "Opções de fixação",
          standard: "Barras laterais, cantoneiras",
          notes: "Montagem reversível para troca e inversão rápida",
        },
      ],
      caption: "Arames em cunha eletrossoldados por fusão com barras transversais de apoio.",
    },
    features: {
      heading: "Características mecânicas e durabilidade",
      intro: "Aspectos de engenharia que asseguram o desempenho das telas curvas em aplicações industriais severas.",
      items: [
        {
          title: "Separação hidrodinâmica passiva",
          text: "Opera com a inércia do fluido e gravidade, desaguando polpas com alta eficiência sem motores ou peças giratórias.",
        },
        {
          title: "Alívio interior contínuo",
          text: "Abertura em V alargada para o interior que assegura descarte rápido dos sólidos que transpõem a abertura.",
        },
        {
          title: "Painel reversível em 180°",
          text: "Permite girar o painel para expor arestas novas, prolongando a vida útil em meios abrasivos.",
        },
        {
          title: "Solda contínua por resistência",
          text: "Fusão de alta amperagem em cada cruzamento para resistir a vibrações intensas e fluxo contínuo de material.",
        },
      ],
    },
    related: {
      heading: "Configurações correlatas de arame em cunha",
      intro: "Conheça outras variantes de separação sólida com arame em cunha para processos industriais.",
      cards: [
        {
          title: "Telas curvas Sieve Bend",
          text: "Conjuntos para desaguamento estático e classificação de polpas em mineração e indústria de amido.",
          to: "/SieveBendScreensPage",
          badge: "Conjuntos curvos",
          linkLabel: "Ver Sieve Bend",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
            alt: "Painel de tela curva Sieve Bend",
          },
        },
        {
          title: "Telas planas em cunha",
          text: "Painéis planos reforçados para peneiras vibratórias, calhas de drenagem e mesas de desaguamento.",
          to: "/FlatPanelScreensPage",
          badge: "Alternativa plana",
          linkLabel: "Ver telas planas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Painel plano de arame em cunha",
          },
        },
        {
          title: "Cilindros de arame em cunha",
          text: "Tambores rotativos e cestos cilíndricos com fenda axial interna ou circunferencial externa.",
          to: "/CylindricalScreensPage",
          badge: "Cilindros e tambores",
          linkLabel: "Ver cilindros",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Cilindros de arame em cunha",
          },
        },
        {
          title: "Tubos filtrantes em cunha",
          text: "Tubos de fenda contínua com alta resistência ao colapso para poços de captação e processos industriais.",
          to: "/ScreenTubesPage",
          badge: "Elementos tubulares",
          linkLabel: "Ver tubos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Tubos de arame em cunha",
          },
        },
        {
          title: "Bicos injetores e crepinas",
          text: "Bicos com rosca para distribuição equilibrada e retenção de resinas em fundos falsos de vasos.",
          to: "/FilterNozzlesPage",
          badge: "Sistemas de drenagem",
          linkLabel: "Ver bicos e crepinas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Bicos injetores em cunha",
          },
        },
        {
          title: "Visão geral arame em cunha",
          text: "Visão abrangente de perfis em cunha, aberturas de 0,02 a 5,0 mm e principais configurações.",
          to: "/WedgeWireScreenPage",
          badge: "Categoria principal",
          linkLabel: "Ver categoria geral",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
            alt: "Fabricação de telas em cunha",
          },
        },
      ],
    },
    cta: {
      id: "curved-screens-cta",
      eyebrow: "Suporte de engenharia",
      heading: "Consulte especificações de telas curvas",
      lead: "Envie suas medidas de raio, arco, largura e abertura de fenda para avaliação técnica de nossa equipe de vendas.",
      secondaryLabel: "Voltar para arame em cunha",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Ligas padrão AISI 304 e AISI 316L. Certificados de matéria-prima disponíveis quando especificados no pedido.",
    },
  },
};
